import {
  DocumentNode,
  NetworkStatus,
  gql,
  useMutation,
  useQuery,
} from '@apollo/client';
import {
  Box,
  Button,
  ButtonGroup,
  Center,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Spinner,
  Stack,
  Text,
  useBoolean,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import * as React from 'react';

import { resource_searched } from '@/analytics';
import { DeleteLibraryResourceModal } from '@/components/LibraryResourceModals/DeleteLibraryResourceModal';
import { PreviewRendererProps } from '@/components/PreviewRenderer/PreviewRenderer';
import { PREVIEW_RENDERER_VERSION_FILE_FRAGMENT } from '@/components/PreviewRenderer/PreviewRendererFragment';
import { logError } from '@/imports/logging/ClientLogger';
import { Card } from '@/imports/ui/chakra/components/Card';
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  FolderPlusIcon,
  LinkIcon,
} from '@/imports/ui/chakra/feather';
import { BrowseItemsList } from '@/imports/ui/components/resource/BrowseItemsList';
import { BulkMoveLibraryFilesModal } from '@/imports/ui/components/resource/BulkMoveLibraryFilesModal';
import { useDebouncedValue } from '@/imports/ui/hooks/useDebouncedValue';
import { useSelectionState } from '@/imports/ui/useSelectionState';

import { GroupResourcesPaneContent_singleVersionFileFragment } from './graphql-types/GroupResourcesPaneContent_singleVersionFileFragment';
import {
  GroupResourcesPane_LibraryFolderContents,
  GroupResourcesPane_LibraryFolderContentsVariables,
} from './graphql-types/GroupResourcesPane_LibraryFolderContents';
import {
  GroupResourcesPane_addFilesToGroupLibrary,
  GroupResourcesPane_addFilesToGroupLibraryVariables,
} from './graphql-types/GroupResourcesPane_addFilesToGroupLibrary';

import { AddLinkModal } from './components/AddLinkModal/AddLinkModal';
import { LibrarySearchBar } from './components/LibrarySearchBar';
import {
  DEFAULT_LIBRARY_SORT,
  LibrarySortDropdown,
  LibrarySortValue,
  toLibraryFolderContentsSortInput,
} from './components/LibrarySortDropdown';
import { LibraryUploadInput } from './components/LibraryUploadInput';
import { NewFolderModal } from './components/NewFolderModal';

const ITEMS_PER_PAGE = 20;

const mergeLibraryItemsById = <T extends { _id?: string | null }>(
  previous: T[] | null | undefined,
  next: T[] | null | undefined
): T[] => {
  const merged = [...(previous || []), ...(next || [])];
  const seen = new Set<string>();
  return merged.filter((item) => {
    if (!item._id || seen.has(item._id)) {
      return false;
    }
    seen.add(item._id);
    return true;
  });
};

const GET_GROUP_LIBRARY_FOLDER_CONTENTS = gql`
  query GroupResourcesPane_LibraryFolderContents(
    $input: GroupInput!
    $folderInput: LibraryFolderContentsInput!
  ) {
    group(input: $input) {
      result {
        libraryFolderContents(input: $folderInput) {
          hasMore
          totalCount
          currentFolder {
            _id
            name
            parentFolder {
              _id
              name
              parentFolder {
                _id
              }
            }
          }
          results {
            ...BrowseItemsList_libraryFolderResultsFragment
          }
        }
      }
    }
  }
  ${BrowseItemsList.libraryFolderResultsFragment}
`;

const ADD_FILES_TO_GROUP_LIBRARY_AST = gql`
  mutation GroupResourcesPane_addFilesToGroupLibrary(
    $input: AddFilesToGroupLibraryInput!
  ) {
    addFilesToGroupLibrary(input: $input)
  }
`;

type IGroupResourcesPaneDropdown = {
  loading: boolean;
  numFilesSelected: number;
  onClickMove: () => void;
  onUploaded: (options: { versionFileIds: string[] }) => void;
  onClickNewFolder?: () => void;
  onClickBulkDeleteFiles: () => void;
  hasCurrentSearch: boolean;
  onClickAddLink: () => void;
};

const GroupResourcesPaneDropdown: React.FC<IGroupResourcesPaneDropdown> = ({
  loading,
  numFilesSelected,
  onClickMove,
  onUploaded,
  onClickNewFolder,
  onClickBulkDeleteFiles,
  hasCurrentSearch,
  onClickAddLink,
}) => {
  const [isLoadingAddFiles, setIsLoadingFiles] = useBoolean(false);

  const isLoading = loading || isLoadingAddFiles;

  return (
    <ButtonGroup>
      {numFilesSelected > 0 && (
        <>
          <Button onClick={onClickBulkDeleteFiles}>Delete</Button>
          <Button onClick={onClickMove}>Move</Button>
        </>
      )}
      {!hasCurrentSearch && (
        <Menu aria-label="resources actions">
          <MenuButton
            as={Button}
            variant={numFilesSelected > 0 ? 'outline' : 'solid'}
            rightIcon={<ChevronDownIcon />}
            isLoading={isLoading}
            minWidth="85px"
          >
            Add
          </MenuButton>
          <Portal>
            <MenuList>
              <LibraryUploadInput
                onStartUploads={setIsLoadingFiles.on}
                onUploaded={(options) => {
                  onUploaded({
                    versionFileIds: options.versionFileIds,
                  });
                }}
                onEndUploads={setIsLoadingFiles.off}
                content={<Text>Add Files</Text>}
              />

              <MenuItem
                icon={<FolderPlusIcon color="gray.600" />}
                onClick={onClickNewFolder}
              >
                New Folder
              </MenuItem>
              <MenuItem
                icon={<LinkIcon color="gray.600" />}
                onClick={onClickAddLink}
              >
                Add Link
              </MenuItem>
            </MenuList>
          </Portal>
        </Menu>
      )}
    </ButtonGroup>
  );
};

/* we can't load parts of react-dom-preview on the server  */
const PreviewRendererModal = dynamic<PreviewRendererProps>(
  () =>
    import('@/components/PreviewRenderer/PreviewRenderer').then(
      (mod) => mod.PreviewRenderer
    ),
  {
    ssr: false,
  }
);

export const GroupResourcesPaneContent: React.FC<{
  currentUserCanEditLibrary: boolean;
  library: {
    _id: string;
    rootFolder: {
      _id: string;
    };
  };
  groupId: string;
  singleVersionFile?: GroupResourcesPaneContent_singleVersionFileFragment | null;
  initialFolderId: string;
}> & { singleVersionFileFragment: DocumentNode } = (props) => {
  const toast = useToast();
  const deleteFilesModal = useDisclosure();
  const newFolderModal = useDisclosure();
  const addLinkModal = useDisclosure();
  const router = useRouter();
  const rootFolderId = props.library.rootFolder._id;
  const [currentFolderId, setCurrentFolderId] = React.useState<string>(
    props.initialFolderId
  );

  React.useEffect(() => {
    setCurrentFolderId(props.initialFolderId);
  }, [props.initialFolderId]);

  const [bulkMoveFilesModalOpen, setBulkMoveFilesModalOpen] =
    React.useState(false);
  const [addFilesFunc, { loading: addFilesLoading }] = useMutation<
    GroupResourcesPane_addFilesToGroupLibrary,
    GroupResourcesPane_addFilesToGroupLibraryVariables
  >(ADD_FILES_TO_GROUP_LIBRARY_AST, {
    awaitRefetchQueries: true,
    refetchQueries: ['GroupResourcesPane_LibraryFolderContents'],
  });

  const [searchText, setSearchText] = React.useState('');
  const debouncedSearchText = useDebouncedValue(searchText, 500);
  const [sortValue, setSortValue] = React.useState<LibrarySortValue>(
    DEFAULT_LIBRARY_SORT
  );
  const loadMoreRef = React.useRef<HTMLDivElement | null>(null);
  const isLoadingMoreRef = React.useRef(false);
  const folderRequestKeyRef = React.useRef('');

  const folderInput = React.useMemo(
    () => ({
      limit: ITEMS_PER_PAGE,
      offset: 0,
      ...toLibraryFolderContentsSortInput(sortValue),
      ...(currentFolderId !== rootFolderId && {
        libraryFolderId: currentFolderId,
      }),
      ...(debouncedSearchText && { searchTerm: debouncedSearchText }),
    }),
    [sortValue, currentFolderId, rootFolderId, debouncedSearchText]
  );
  const folderRequestKey = JSON.stringify({
    ...folderInput,
    offset: undefined,
  });
  folderRequestKeyRef.current = folderRequestKey;

  const { error, data, networkStatus, fetchMore } = useQuery<
    GroupResourcesPane_LibraryFolderContents,
    GroupResourcesPane_LibraryFolderContentsVariables
  >(GET_GROUP_LIBRARY_FOLDER_CONTENTS, {
    variables: {
      input: {
        _id: props.groupId,
      },
      folderInput,
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const isInitialLoading =
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.setVariables;
  const isFetchingMore = networkStatus === NetworkStatus.fetchMore;

  const libraryFolderContents = data?.group.result?.libraryFolderContents;
  const loadedCount =
    (libraryFolderContents?.results?.folders?.length || 0) +
    (libraryFolderContents?.results?.files?.length || 0) +
    (libraryFolderContents?.results?.links?.length || 0);
  const hasMore = !!libraryFolderContents?.hasMore;

  const loadMore = React.useCallback(async () => {
    if (
      !hasMore ||
      isInitialLoading ||
      isFetchingMore ||
      loadedCount === 0 ||
      isLoadingMoreRef.current
    ) {
      return;
    }

    const requestKey = folderRequestKey;
    isLoadingMoreRef.current = true;
    try {
      await fetchMore({
        variables: {
          input: {
            _id: props.groupId,
          },
          folderInput: {
            ...folderInput,
            offset: loadedCount,
          },
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (folderRequestKeyRef.current !== requestKey || !fetchMoreResult) {
            return previousResult;
          }

          const previousContents =
            previousResult.group?.result?.libraryFolderContents;
          const nextContents =
            fetchMoreResult.group?.result?.libraryFolderContents;
          const nextCount =
            (nextContents?.results?.folders?.length || 0) +
            (nextContents?.results?.files?.length || 0) +
            (nextContents?.results?.links?.length || 0);

          if (!previousContents || !nextContents || nextCount === 0) {
            if (!previousContents) {
              return previousResult;
            }
            return {
              ...previousResult,
              group: {
                ...previousResult.group,
                result: previousResult.group?.result
                  ? {
                      ...previousResult.group.result,
                      libraryFolderContents: {
                        ...previousContents,
                        hasMore: false,
                      },
                    }
                  : previousResult.group?.result,
              },
            };
          }

          return {
            ...previousResult,
            group: {
              ...previousResult.group,
              result: previousResult.group?.result
                ? {
                    ...previousResult.group.result,
                    libraryFolderContents: {
                      ...nextContents,
                      results: {
                        ...nextContents.results,
                        folders: mergeLibraryItemsById(
                          previousContents.results.folders,
                          nextContents.results.folders
                        ),
                        files: mergeLibraryItemsById(
                          previousContents.results.files,
                          nextContents.results.files
                        ),
                        links: mergeLibraryItemsById(
                          previousContents.results.links,
                          nextContents.results.links
                        ),
                      },
                    },
                  }
                : previousResult.group?.result,
            },
          };
        },
      });
    } finally {
      isLoadingMoreRef.current = false;
    }
  }, [
    fetchMore,
    folderInput,
    folderRequestKey,
    hasMore,
    isFetchingMore,
    isInitialLoading,
    loadedCount,
    props.groupId,
  ]);

  React.useEffect(() => {
    const node = loadMoreRef.current;
    if (!node || isInitialLoading || !hasMore) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];
        if (!firstEntry?.isIntersecting || isLoadingMoreRef.current) {
          return;
        }
        void loadMore();
      },
      {
        root: null,
        rootMargin: '200px',
        threshold: 0,
      }
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, isInitialLoading, loadMore]);

  if (error) {
    throw error;
  }

  const addToGroupLibraryHandler = async (options: {
    versionFileIds: string[];
  }): Promise<void> => {
    try {
      if (!currentFolderId) return;

      await addFilesFunc({
        variables: {
          input: {
            versionFileIds: options.versionFileIds,
            libraryFolderId: currentFolderId || '',
            groupId: props.groupId,
          },
        },
      });
    } catch (err) {
      logError(err);
      toast({ status: 'error', description: 'Failed to add file.' });
    }
  };

  const currentFolder = libraryFolderContents?.currentFolder;
  const totalCount = libraryFolderContents?.totalCount || 0;
  const lastTrackedSearchRef = React.useRef<string>();

  React.useEffect(() => {
    if (
      debouncedSearchText.trim().length < 2 ||
      networkStatus !== NetworkStatus.ready
    )
      return;
    const signature = `${props.groupId}:${currentFolderId}:${debouncedSearchText}:${totalCount}`;
    if (lastTrackedSearchRef.current === signature) return;
    lastTrackedSearchRef.current = signature;
    resource_searched({
      entity_id: props.groupId,
      entity_type: 'group_library',
      group_id: props.groupId,
      resource_folder_id: currentFolderId,
      search_query: debouncedSearchText,
      search_query_length: debouncedSearchText.length,
      search_type: 'resource',
      search_result_count: totalCount,
      search_has_results: totalCount > 0,
    });
  }, [
    currentFolderId,
    debouncedSearchText,
    networkStatus,
    props.groupId,
    totalCount,
  ]);

  const folderResults = (libraryFolderContents?.results?.folders || []).filter(
    (folder) => folder._id
  );
  const fileLinks = libraryFolderContents?.results?.files || [];
  const links = libraryFolderContents?.results?.links || [];
  const finalLinkResults = fileLinks.filter(
    (link) => link.versionFile && link.versionFile.file
  );

  // API returns folder-file *links*. The same VersionFile can appear more than
  // once; keep the first link so sorting does not render duplicate rows.
  const uniqueFileLinks = React.useMemo(() => {
    const seenVersionFileIds = new Set<string>();
    return finalLinkResults.filter((link) => {
      const versionFileId = link.versionFile?._id;
      if (!versionFileId || seenVersionFileIds.has(versionFileId)) {
        return false;
      }
      seenVersionFileIds.add(versionFileId);
      return true;
    });
  }, [finalLinkResults]);

  // Server owns sort order via `position` (Manual = libraryItemPositions;
  // Name/Date/Created-by = field sort index). Client only reassembles the
  // three result arrays into that order for rendering + pagination merges.
  const orderedItems = React.useMemo(() => {
    const items = [
      ...folderResults.map((folder) => ({
        type: 'folder' as const,
        folder,
        position: folder.position,
      })),
      ...links.map((link) => ({
        type: 'link' as const,
        link,
        position: link.position,
      })),
      ...uniqueFileLinks.map((link) => ({
        type: 'file' as const,
        versionFile: link.versionFile!,
        fileLinkId: link._id,
        position: link.position,
      })),
    ];

    items.sort((left, right) => {
      const leftPos =
        typeof left.position === 'number'
          ? left.position
          : Number.POSITIVE_INFINITY;
      const rightPos =
        typeof right.position === 'number'
          ? right.position
          : Number.POSITIVE_INFINITY;
      return leftPos - rightPos;
    });

    return items.map((item) => {
      if (item.type === 'folder') {
        return { type: 'folder' as const, folder: item.folder };
      }
      if (item.type === 'link') {
        return { type: 'link' as const, link: item.link };
      }
      return {
        type: 'file' as const,
        versionFile: item.versionFile,
        fileLinkId: item.fileLinkId,
      };
    });
  }, [folderResults, links, uniqueFileLinks]);

  const { selection, selectAll, selectNone, toggleOne } = useSelectionState<{
    _id: string;
  }>((item) => item._id);

  const allVersionFileIds = uniqueFileLinks.flatMap((link) =>
    !!link.versionFile ? [link.versionFile._id] : []
  );
  // TODO (Lily): Currently all select only works for current page, would need to make backend changes to support
  // true select all
  const selectedVersionFileIds = allVersionFileIds.filter((id) =>
    selection?.allSelected
      ? !selection.excludedItems.map((item) => item._id).includes(id)
      : selection.includedItems.map((item) => item._id).includes(id)
  );

  React.useEffect(() => selectNone(), [
    debouncedSearchText,
    sortValue,
    selectNone,
  ]);

  if (!rootFolderId) {
    return null;
  }

  const navigateToFolderHandler = (folderId: string): void => {
    setCurrentFolderId(folderId);
    if (folderId === rootFolderId) {
      const { folderId, ...query } = router.query;
      router.push({ pathname: router.pathname, query: query }, undefined, {
        shallow: true,
      });
    } else {
      router.push(
        {
          pathname: router.pathname,
          query: { ...router.query, folderId: folderId },
        },
        undefined,
        { shallow: true }
      );
    }
    selectNone();
    setSearchText('');
  };

  return (
    <>
      <BulkMoveLibraryFilesModal
        groupId={props.groupId}
        versionFileIds={selectedVersionFileIds}
        open={bulkMoveFilesModalOpen}
        onClose={() => setBulkMoveFilesModalOpen(false)}
        rootFolderId={rootFolderId}
        onAfterMove={selectNone}
      />

      {currentFolder?._id && (
        <NewFolderModal
          groupId={props.groupId}
          parentLibraryFolderId={currentFolder._id}
          isOpen={newFolderModal.isOpen}
          onClose={newFolderModal.onClose}
        />
      )}

      {currentFolder?._id && (
        <AddLinkModal
          groupId={props.groupId}
          parentLibraryFolderId={currentFolder._id}
          isOpen={addLinkModal.isOpen}
          onClose={addLinkModal.onClose}
          onCompleted={selectNone}
        />
      )}

      <DeleteLibraryResourceModal
        groupId={props.groupId}
        versionFileIds={selectedVersionFileIds}
        isOpen={deleteFilesModal.isOpen}
        onClose={deleteFilesModal.onClose}
        onAfterDeleteFiles={selectNone}
      />

      {props.singleVersionFile &&
        networkStatus !== NetworkStatus.loading &&
        networkStatus !== NetworkStatus.setVariables &&
        !allVersionFileIds.includes(props.singleVersionFile._id) && (
          <PreviewRendererModal
            versionFile={props.singleVersionFile}
            onClose={() => {
              const { versionFileId, ...query } = router.query;
              router.replace(
                { pathname: router.pathname, query: query },
                undefined,
                { shallow: true }
              );
            }}
            isOpen={router.query.versionFileId === props.singleVersionFile._id}
            linkOriginalPost
          />
        )}

      {currentFolder &&
        currentFolder.parentFolder &&
        networkStatus !== NetworkStatus.loading &&
        networkStatus !== NetworkStatus.setVariables && (
          <Stack>
            <Box>
              <Button
                leftIcon={<ChevronLeftIcon />}
                variant="link"
                colorScheme="black"
                onClick={() => {
                  currentFolder.parentFolder &&
                    navigateToFolderHandler(currentFolder.parentFolder._id);
                }}
              >
                Back to{' '}
                {currentFolder.parentFolder.name === '_root'
                  ? 'Resources'
                  : currentFolder.parentFolder.name}
              </Button>
            </Box>
            <Text textStyle="xlBodyStrong">{currentFolder.name}</Text>
          </Stack>
        )}

      <Card px="6" py="4">
        <Stack spacing="3">
          <HStack spacing="4">
            <Box flex="1" minW="0">
              <LibrarySearchBar
                value={searchText}
                folderName={
                  currentFolder && currentFolder._id !== rootFolderId
                    ? currentFolder.name
                    : ''
                }
                onTextChange={(value) => {
                  setSearchText(value);
                }}
                loading={
                  !!searchText && networkStatus === NetworkStatus.setVariables
                }
              />
            </Box>
            {props.currentUserCanEditLibrary && (
              <LibrarySortDropdown
                value={sortValue}
                onChange={(nextSort) => {
                  setSortValue(nextSort);
                }}
              />
            )}
            {props.currentUserCanEditLibrary && (
              <GroupResourcesPaneDropdown
                onUploaded={addToGroupLibraryHandler}
                onClickMove={() => setBulkMoveFilesModalOpen(true)}
                onClickNewFolder={newFolderModal.onOpen}
                numFilesSelected={selectedVersionFileIds.length}
                loading={addFilesLoading}
                onClickBulkDeleteFiles={deleteFilesModal.onOpen}
                hasCurrentSearch={!!searchText}
                onClickAddLink={addLinkModal.onOpen}
              />
            )}
          </HStack>
          {props.currentUserCanEditLibrary && (
            <Text textStyle="compact" color="gray.600">
              {sortValue === 'manual'
                ? 'Drag rows using the grip handle to create a custom order.'
                : 'Sorting is active. Switch to Manual order to drag and rearrange rows.'}
            </Text>
          )}
        </Stack>
      </Card>

      <BrowseItemsList
        groupId={props.groupId}
        items={orderedItems}
        rootFolderId={rootFolderId}
        libraryFolderId={currentFolder?._id || rootFolderId}
        selection={selection}
        selectAll={selectAll}
        selectNone={selectNone}
        toggleOne={toggleOne}
        navigateToFolder={navigateToFolderHandler}
        currentUserCanEditGroupLibrary={props.currentUserCanEditLibrary}
        isManualOrder={sortValue === 'manual' && !debouncedSearchText}
        loading={isInitialLoading}
      />

      {hasMore && !isInitialLoading && (
        <Center ref={loadMoreRef} py="4" minH="10">
          {isFetchingMore && <Spinner size="sm" color="gray.500" />}
        </Center>
      )}
    </>
  );
};

GroupResourcesPaneContent.singleVersionFileFragment = gql`
  fragment GroupResourcesPaneContent_singleVersionFileFragment on VersionFile {
    _id
    ...PreviewRenderer_VersionFileFragment
  }
  ${PREVIEW_RENDERER_VERSION_FILE_FRAGMENT}
`;
